import type { Competition, Translated } from '@uefadigital/panenka';
import clsx from 'clsx';
import { memo, useMemo } from 'react';

import { hydrateWithTranslations } from '@services/TranslatorService';
import Image from '@shared/Image';
import { PkTheme } from '@shared-types/common.types';
import { RoundPhase } from '@shared-types/match.types';
import { imgBaseUrl } from '@utilities/WindowContext';

import * as styles from './styles.module.scss';

const CompetitionRoofline = memo(({ competition, theme }: { competition: Translated<Competition>; theme: PkTheme }) => {
  const compDisplayName = useMemo(() => {
    const phaseNameMap = {
      [RoundPhase.QUALIFYING]: competition?.qualifyingName,
      [RoundPhase.PREQUALIFYING]: competition?.prequalifyingName,
      default: competition?.name,
    } as const;

    return phaseNameMap[competition?.phase?.toUpperCase()] ?? phaseNameMap['default'];
  }, [competition]);

  const compCode = useMemo(() => {
    if (competition?.id === '19') return 'friendlies';
    return competition?.phase && competition.phase?.toUpperCase() !== RoundPhase.TOURNAMENT
      ? `${competition.code.toLowerCase()}_${competition.phase.toLowerCase()}`
      : competition?.code?.toLowerCase();
  }, [competition?.code, competition?.id, competition.phase]);

  return (
    <div
      className={clsx(
        'pk-d--flex',
        'pk-align-items--center',
        theme === PkTheme.DARK && 'pk-p--xs2 pk-pr--s',
        theme === PkTheme.DARK && styles.rooflineComp,
      )}
    >
      <Image
        className='pk-flex--shrink-0 pk-mr--xs2'
        src={`${imgBaseUrl}/imgml/uefacom/elements/logos/competitions/${theme}/${compCode}` + '.svg'}
        width={24}
        height={24}
        alt={compCode}
        title={compDisplayName}
      />
      <span
        className={clsx(
          'pk-ml--xs3 pk-text--text-01 pk-font--base-semibold pk-font-size--s pk-line-height--md',
          styles.rooflineCompName,
        )}
      >
        {compDisplayName}
      </span>
    </div>
  );
});
CompetitionRoofline.displayName = 'CompetitionRoofline';

const Roofline = ({ roofline, theme }: { roofline: string | Competition; theme: PkTheme }) => {
  if (typeof roofline === 'string') {
    return <span className={clsx(styles.roofline, styles[roofline])}>{roofline}</span>;
  }

  const competition = hydrateWithTranslations(roofline);

  return <CompetitionRoofline competition={competition} theme={theme} />;
};

export default memo(Roofline);
